import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Save, Send, Plus, MessageSquare, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

const StatCard = ({ label, value, accent, testid }) => (
  <Card className="p-4 sm:p-5 border-slate-200" data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
      {label}
    </p>
    <p className={`font-heading text-3xl font-extrabold mt-1.5 ${accent}`}>{value}</p>
  </Card>
);

export default function Sms() {
  const [settings, setSettings] = useState({ message_template: "", message_template_hi: "" });
  const [configured, setConfigured] = useState(false);
  const [autoSend, setAutoSend] = useState(true);
  const [variables, setVariables] = useState([]);
  const [data, setData] = useState({ logs: [], total_registrations: 0, sent_count: 0, pending_count: 0 });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resendId, setResendId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logPage, setLogPage] = useState(1);
  const logPageSize = 10;
  const msgRef = useRef(null);
  const msgRefHi = useRef(null);

  const loadSettings = () =>
    api.get("/sms/settings").then((r) => {
      setSettings({
        message_template: r.data.message_template || "",
        message_template_hi: r.data.message_template_hi || "",
      });
      setVariables(r.data.variables || []);
      setConfigured(!!r.data.configured);
      setAutoSend(r.data.auto_send !== false);
    });

  const loadLogs = () => api.get("/sms/logs").then((r) => setData(r.data));

  useEffect(() => {
    Promise.all([loadSettings(), loadLogs()]).finally(() => setLoading(false));
  }, []);

  const insertVariable = (v, field, ref) => {
    const token = `{${v}}`;
    const el = ref.current;
    const cur = settings[field] || "";
    if (!el) {
      setSettings((s) => ({ ...s, [field]: (s[field] || "") + token }));
      return;
    }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setSettings((s) => ({ ...s, [field]: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/sms/settings", {
        message_template: settings.message_template,
        message_template_hi: settings.message_template_hi,
      });
      toast.success("Message templates saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save templates");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoSend = async (val) => {
    setAutoSend(val);
    try {
      await api.put("/sms/auto-send", { enabled: val });
      toast.success(val ? "Auto-send enabled" : "Auto-send disabled");
    } catch (err) {
      setAutoSend(!val);
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update auto-send");
    }
  };

  const sendPending = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/sms/send-pending");
      toast.success(`SMS sent: ${res.sent}, failed: ${res.failed}`);
      loadLogs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  const resend = async (log) => {
    setResendId(log.id);
    try {
      const { data: res } = await api.post(`/sms/resend/${log.reg_pk}`);
      toast.success(res.sent ? "SMS resent" : "Resend failed");
      loadLogs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to resend");
    } finally {
      setResendId(null);
    }
  };

  const logTotalPages = Math.max(1, Math.ceil(data.logs.length / logPageSize));
  const logCurrentPage = Math.min(logPage, logTotalPages);
  const pagedLogs = data.logs.slice(
    (logCurrentPage - 1) * logPageSize,
    logCurrentPage * logPageSize
  );

  return (
    <div className="space-y-6" data-testid="sms-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
            SMS Notifications
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send registration IDs to registered persons and track delivery.
          </p>
        </div>
        <Button
          onClick={sendPending}
          disabled={sending || data.pending_count === 0}
          className="bg-blue-900 hover:bg-blue-800"
          data-testid="sms-send-pending-btn"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Send to Pending ({data.pending_count})
        </Button>
      </div>

      {loading ? (
        <PageLoader label="Loading SMS data…" />
      ) : (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="sms-stat-total" label="Total Registrations" value={data.total_registrations} accent="text-slate-900" />
        <StatCard testid="sms-stat-sent" label="SMS Sent" value={data.sent_count} accent="text-emerald-600" />
        <StatCard testid="sms-stat-pending" label="Pending" value={data.pending_count} accent="text-amber-600" />
      </div>

      <Card className="p-5 sm:p-6 border-slate-200">
        <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-900" /> Message Template
        </h2>

        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2 text-sm">
          <Badge
            className={
              configured
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                : "bg-amber-100 text-amber-700 hover:bg-amber-100"
            }
            data-testid="sms-credentials-status"
          >
            {configured ? "Credentials set" : "Credentials not set"}
          </Badge>
          <span className="text-muted-foreground">
            API Key &amp; Sender ID are managed directly in the database.
          </span>
        </div>

        <div className="mb-4 rounded-lg border border-slate-200 px-4 py-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-900">Auto-send on new registration</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              When on, an SMS is sent automatically as soon as a person registers.
            </p>
          </div>
          <Switch
            checked={autoSend}
            onCheckedChange={toggleAutoSend}
            data-testid="sms-auto-send-switch"
          />
        </div>

        <div>
          <Label htmlFor="message">Message (English)</Label>
          <div className="flex flex-wrap gap-2 mt-1.5 mb-2">
            {variables.map((v) => (
              <Button
                key={v}
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => insertVariable(v, "message_template", msgRef)}
                data-testid={`sms-var-${v}`}
              >
                <Plus className="h-3 w-3 mr-1" /> {`{${v}}`}
              </Button>
            ))}
          </div>
          <Textarea
            id="message"
            ref={msgRef}
            data-testid="sms-message-input"
            value={settings.message_template}
            onChange={(e) => setSettings((s) => ({ ...s, message_template: e.target.value }))}
            placeholder="e.g. Dear {name}, your registration ID is {registration_id} for {municipality}."
            rows={4}
            className="bg-white font-mono text-sm"
          />
        </div>

        <div className="mt-4">
          <Label htmlFor="message-hi">Message (Hindi / हिंदी)</Label>
          <div className="flex flex-wrap gap-2 mt-1.5 mb-2">
            {variables.map((v) => (
              <Button
                key={`hi-${v}`}
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => insertVariable(v, "message_template_hi", msgRefHi)}
                data-testid={`sms-var-hi-${v}`}
              >
                <Plus className="h-3 w-3 mr-1" /> {`{${v}}`}
              </Button>
            ))}
          </div>
          <Textarea
            id="message-hi"
            ref={msgRefHi}
            data-testid="sms-message-hi-input"
            value={settings.message_template_hi}
            onChange={(e) => setSettings((s) => ({ ...s, message_template_hi: e.target.value }))}
            placeholder="जैसे: प्रिय {name}, {municipality} के लिए आपका पंजीकरण आईडी {registration_id} है।"
            rows={4}
            className="bg-white text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Click a variable to insert it. The Hindi SMS is sent with the provider's
            <span className="font-mono"> unicode=2</span> parameter. Leave blank to skip Hindi.
          </p>
        </div>

        <div className="mt-2 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800">
          Each registered person receives <strong>both</strong> SMS (English &amp; Hindi) when both templates are filled.
        </div>

        <div className="mt-4">
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="sms-save-settings-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Templates
          </Button>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="p-5 sm:p-6 pb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            Delivery Log
          </h2>
          <Button variant="outline" size="sm" onClick={loadLogs} data-testid="sms-refresh-logs-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-2" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="sms-logs-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Registration ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>API Response</TableHead>
                <TableHead>Sent At</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-10">
                    No SMS sent yet.
                  </TableCell>
                </TableRow>
              ) : (
                pagedLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs font-semibold text-blue-900">
                      {log.registration_id}
                    </TableCell>
                    <TableCell className="font-medium">{log.name}</TableCell>
                    <TableCell className="font-mono text-sm">{log.phone}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          log.lang === "hi"
                            ? "border-orange-200 bg-orange-50 text-orange-700"
                            : "border-slate-200 bg-slate-50 text-slate-700"
                        }
                        data-testid={`sms-lang-${log.id}`}
                      >
                        {log.lang === "hi" ? "हिंदी" : "English"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          log.status === "sent"
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"
                            : "bg-rose-100 text-rose-700 hover:bg-rose-100"
                        }
                        data-testid={`sms-status-${log.id}`}
                      >
                        {log.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      <code className="text-[11px] text-slate-600 break-all line-clamp-2 block">
                        {JSON.stringify(log.response)}
                      </code>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => resend(log)}
                        disabled={resendId === log.id}
                        data-testid={`sms-resend-${log.id}`}
                        title="Resend SMS"
                      >
                        {resendId === log.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {data.logs.length > 0 && (
          <div
            className="flex items-center justify-between px-5 sm:px-6 py-4 border-t border-slate-200"
            data-testid="sms-logs-pagination"
          >
            <p className="text-sm text-muted-foreground">
              Showing {(logCurrentPage - 1) * logPageSize + 1}–
              {Math.min(logCurrentPage * logPageSize, data.logs.length)} of {data.logs.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                disabled={logCurrentPage <= 1}
                data-testid="sms-logs-pagination-prev-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Prev
              </Button>
              <span className="text-sm font-medium px-2" data-testid="sms-logs-pagination-page-info">
                Page {logCurrentPage} of {logTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))}
                disabled={logCurrentPage >= logTotalPages}
                data-testid="sms-logs-pagination-next-btn"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
